import { bodiesOverlap, bottomForCenterY, centerX, centerY, clampAbs, pctOf } from '../geom'
import { NO_PLAYER } from '../world'
import type { PlayerState, World } from '../world'
import { hit } from './hit'
import type { ProjectileItem } from './types'

/** Aim point: `aimPct` of the standing height below the player's top (50 = body centre). */
function aimY(world: World, p: PlayerState): number {
  return p.y - p.h + pctOf(world.cfg.playerHeightPx, world.cfg.items.homingDrone.aimPct)
}

/**
 * The racing player directly ahead of `i`: smallest `progress` strictly greater than the
 * owner's (not the leader). Ties go to the lower index. `NO_PLAYER` when nobody is ahead.
 */
export function droneTarget(world: World, i: number): number {
  const own = world.players[i]!.progress
  let best = NO_PLAYER
  for (let k = 0; k < world.players.length; k++) {
    const p = world.players[k]!
    if (k === i || p.placement !== 0 || p.progress <= own) continue
    if (best === NO_PLAYER || p.progress < world.players[best]!.progress) best = k
  }
  return best
}

/**
 * Homing Drone: flies (no terrain) from the owner's front edge toward its target's body centre
 * with separate horizontal and vertical speed limits. Within `lockPx` horizontally it commits:
 * full speed in its current direction, height locked, so a late jump lifts the target over it
 * (D-044). It
 * hits only its target; it expires after `lifetimeTicks` or when the target finishes. With no
 * target it flies straight right until it expires.
 */
export const homingDrone: ProjectileItem = {
  id: 'homing_drone',
  use(world, i) {
    const p = world.players[i]!
    const hd = world.cfg.items.homingDrone
    const target = droneTarget(world, i)
    world.projectiles.push({
      id: world.nextId++,
      kind: 'homing_drone',
      owner: i,
      x: p.x + p.w,
      y: bottomForCenterY(aimY(world, p), hd.sizePx),
      w: hd.sizePx,
      h: hd.sizePx,
      vx: target === NO_PLAYER ? hd.speedPxPerTick : 0,
      vy: 0,
      grounded: false,
      target,
      locked: false,
      expiresAtTick: world.tick + hd.lifetimeTicks,
    })
  },
  update(world, pr, events) {
    if (world.tick >= pr.expiresAtTick) return false
    if (pr.target === NO_PLAYER) {
      pr.x += pr.vx
      return true
    }
    const t = world.players[pr.target]!
    if (t.placement !== 0) return false
    const hd = world.cfg.items.homingDrone
    if (!pr.locked) {
      const dx = centerX(t) - centerX(pr)
      if (Math.abs(dx) <= hd.lockPx) {
        pr.locked = true
        pr.vx = dx < 0 ? -hd.speedPxPerTick : hd.speedPxPerTick
        pr.vy = 0
      } else {
        pr.vx = clampAbs(dx, hd.speedPxPerTick)
        pr.vy = clampAbs(aimY(world, t) - centerY(pr), hd.verticalSpeedPxPerTick)
      }
    }
    pr.x += pr.vx
    pr.y += pr.vy
    if (!bodiesOverlap(t, pr)) return true
    return !hit(world, pr.target, 'homing_drone', hd.stunTicks, events, pr.owner)
  },
}
